import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import {
  getRemoteConfig as getRemoteConfigSDK,
  fetchAndActivate,
  getValue,
  getAll,
  activate,
  type RemoteConfig,
} from 'firebase/remote-config'
import * as remoteConfigApi from 'firebase/remote-config'
import { app, auth } from '@/firebase'
import { analyticsPromise } from '@/utils/analytics'
import createLogger from '@/utils/logger'

type OnConfigUpdateFn = (
  remoteConfig: RemoteConfig,
  observer: {
    next: (update: { getUpdatedKeys: () => Set<string> }) => void | Promise<void>
    error?: (error: unknown) => void
    complete?: () => void
  },
) => () => void

const onConfigUpdate = (remoteConfigApi as Record<string, unknown>).onConfigUpdate as
  | OnConfigUpdateFn
  | undefined

const logger = createLogger('stores/remoteConfig.ts')

// Constants
const DEFAULT_MINIMUM_FETCH_INTERVAL_MS = 12 * 60 * 60 * 1000 // 12 hours
const DEFAULT_FETCH_TIMEOUT_MS = 60 * 1000 // 60 seconds
const FORCE_REFRESH_FALLBACK_MINIMUM_FETCH_INTERVAL_MS = 0 // 0ms for immediate refresh
const DEFAULT_STRING_VALUE = ''
const DEFAULT_NUMBER_VALUE = 0
const DEFAULT_BOOLEAN_VALUE = false
const DEFAULT_TEST_PARAMETER_VALUE = 0
const DEFAULT_REMOTE_CONFIG: Record<string, string | number | boolean> = {
  test_parameter: DEFAULT_TEST_PARAMETER_VALUE,
}
// Firebase Remote Config persists ETag/throttle/last fetch info in IndexedDB under this name.
const REMOTE_CONFIG_IDB_NAME = 'firebase_remote_config'

async function clearRemoteConfigPersistence(): Promise<void> {
  if (typeof indexedDB === 'undefined') {
    return
  }
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open(REMOTE_CONFIG_IDB_NAME)
      req.onsuccess = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        // If the database is empty or has no stores, just close and return
        if (db.objectStoreNames.length === 0) {
          db.close()
          resolve()
          return
        }

        try {
          const transaction = db.transaction(db.objectStoreNames, 'readwrite')
          transaction.onerror = () => {
            // Ignore errors, just close
            db.close()
            resolve()
          }
          transaction.oncomplete = () => {
            db.close()
            resolve()
          }

          for (const storeName of Array.from(db.objectStoreNames)) {
            try {
              transaction.objectStore(storeName).clear()
            } catch (e) {
              logger.warn(`Failed to clear store ${storeName}`, e)
            }
          }
        } catch (_err) {
          // Transaction creation failed (e.g. concurrent access issues)
          db.close()
          resolve()
        }
      }
      req.onerror = () => resolve() // DB might not exist
      req.onblocked = () => resolve()
    } catch {
      resolve()
    }
  })
}

// ---------------------------------------------------------------------------
// Remote Config instance (created after Analytics is initialized)
// ---------------------------------------------------------------------------

function createAndConfigureRemoteConfig(options?: {
  minimumFetchIntervalMillis?: number
}): RemoteConfig {
  const remoteConfig = getRemoteConfigSDK(app)
  remoteConfig.settings.minimumFetchIntervalMillis =
    options?.minimumFetchIntervalMillis ?? DEFAULT_MINIMUM_FETCH_INTERVAL_MS
  remoteConfig.settings.fetchTimeoutMillis = DEFAULT_FETCH_TIMEOUT_MS
  remoteConfig.defaultConfig = DEFAULT_REMOTE_CONFIG
  return remoteConfig
}

async function fetchAndActivateRemoteConfig(
  remoteConfig: RemoteConfig,
  forceRefresh: boolean,
): Promise<void> {
  if (!forceRefresh) {
    await fetchAndActivate(remoteConfig)
    return
  }

  // For force refresh, we bypass cache by temporarily setting minimumFetchIntervalMillis.
  const originalInterval = remoteConfig.settings.minimumFetchIntervalMillis
  const intervalsToTry = Array.from(
    new Set([FORCE_REFRESH_FALLBACK_MINIMUM_FETCH_INTERVAL_MS, originalInterval]),
  )

  try {
    let lastError: unknown
    for (const interval of intervalsToTry) {
      try {
        remoteConfig.settings.minimumFetchIntervalMillis = interval
        await fetchAndActivate(remoteConfig)
        return
      } catch (err) {
        lastError = err
        logger.warn('Remote Config force refresh attempt failed', { interval, err })
      }
    }
    throw lastError
  } finally {
    // Always restore the original interval for normal caching behavior
    remoteConfig.settings.minimumFetchIntervalMillis = originalInterval
  }
}

/**
 * Current promise for the Remote Config instance. Recreated on reset so the next
 * getRemoteConfig() re-initializes the instance with defaultConfig and settings.
 */
let remoteConfigInstancePromise: Promise<RemoteConfig> = analyticsPromise.then(() =>
  createAndConfigureRemoteConfig(),
)

/**
 * Returns the Remote Config instance, ensuring Analytics is initialized first.
 * Use before any Remote Config operations so user-property targeting works.
 */
async function getRemoteConfig(): Promise<RemoteConfig> {
  return remoteConfigInstancePromise
}

/**
 * Resets the Remote Config instance so the next getRemoteConfig() creates a fresh
 * promise and re-runs createAndConfigureRemoteConfig() (re-applies defaults and settings).
 * Sets fetch interval low so the next fetch bypasses cache and gets fresh data.
 * Called from resetForNewUser().
 */
function resetRemoteConfigInstance() {
  remoteConfigInstancePromise = analyticsPromise.then(() => {
    const remoteConfig = createAndConfigureRemoteConfig({
      minimumFetchIntervalMillis: FORCE_REFRESH_FALLBACK_MINIMUM_FETCH_INTERVAL_MS,
    })
    logger.log('Remote Config instance reset with fresh fetch enabled')
    return remoteConfig
  })
}

export const useRemoteConfigStore = defineStore('remoteConfig', () => {
  const isLoading = ref(false)
  const lastFetchTime = ref<Date | null>(null)
  const error = ref<string | null>(null)
  const isActivated = ref(false)
  const testParameter = ref(DEFAULT_TEST_PARAMETER_VALUE)
  const lastFetchedUid = ref<string | null>(null)
  // Track which user the config was fetched for (for debugging)
  const fetchedForUid = ref<string | null>(null)
  // Store the unsubscribe function for the real-time listener
  let realtimeUnsubscribe: (() => void) | null = null

  /**
   * Set up real-time listener for Remote Config updates.
   * Listens for changes from the Remote Config backend and automatically activates them.
   */
  async function setupRealtimeListener(remoteConfig: RemoteConfig) {
    // Clean up existing listener if any
    if (realtimeUnsubscribe) {
      realtimeUnsubscribe()
      realtimeUnsubscribe = null
    }

    if (!onConfigUpdate) {
      logger.log('Remote Config real-time updates are not available in this Firebase SDK version')
      return
    }

    try {
      realtimeUnsubscribe = onConfigUpdate(remoteConfig, {
        next: async (configUpdate) => {
          const updatedKeys = configUpdate.getUpdatedKeys()
          logger.log('Remote Config real-time update received', {
            updatedKeys: Array.from(updatedKeys),
          })

          // Activate the updated config
          try {
            const activated = await activate(remoteConfig)
            if (activated) {
              logger.log('Remote Config real-time update activated', {
                updatedKeys: Array.from(updatedKeys),
              })

              // Update store state
              isActivated.value = true
              lastFetchTime.value = new Date()
              const currentUser = auth.currentUser
              fetchedForUid.value = currentUser?.uid ?? null

              // Update test parameter if it was updated
              if (updatedKeys.has('test_parameter')) {
                try {
                  testParameter.value = getValue(remoteConfig, 'test_parameter').asNumber()
                } catch {
                  // keep default
                }
              }
            } else {
              logger.log('Remote Config real-time update was already activated')
            }
          } catch (activateError) {
            logger.error('Failed to activate Remote Config real-time update', activateError)
            error.value =
              activateError instanceof Error ? activateError.message : 'Failed to activate update'
          }
        },
        error: (err) => {
          logger.error('Remote Config real-time listener error', err)
          error.value = err instanceof Error ? err.message : 'Real-time listener error'
        },
        complete: () => {
          logger.log('Remote Config real-time listener stopped')
          realtimeUnsubscribe = null
        },
      })

      logger.log('Remote Config real-time listener set up successfully')
    } catch (err) {
      logger.warn('Failed to set up Remote Config real-time listener', err)
      // Don't throw - real-time updates are optional
    }
  }

  /**
   * Reset Remote Config state and instance for a new user (e.g. on logout).
   * Call this on sign-out so the next user gets fresh personalized config on login.
   * Clears activated state and cached values, and resets the instance so the next
   * getRemoteConfig() re-initializes with defaultConfig and settings.
   */
  function resetForNewUser() {
    // Clean up real-time listener
    if (realtimeUnsubscribe) {
      realtimeUnsubscribe()
      realtimeUnsubscribe = null
    }

    resetRemoteConfigInstance()
    // Best effort clear persistence on logout
    clearRemoteConfigPersistence().catch((e) =>
      logger.warn('Failed to clear remote config persistence on logout', e),
    )
    isActivated.value = false
    lastFetchTime.value = null
    error.value = null
    testParameter.value = DEFAULT_TEST_PARAMETER_VALUE
    lastFetchedUid.value = null
    fetchedForUid.value = null // Clear fetched user tracking
    logger.log('Remote Config state reset for new user')
  }

  /**
   * Fetch and activate Remote Config values from Firebase.
   * Ensures Analytics is initialized first so user-property targeting works.
   * Call with forceRefresh: true on login so the server gets the current user's
   * token and returns fresh personalized config for that user.
   * @param forceRefresh - If true, bypasses cache and fetches fresh values from server
   */
  async function fetchConfig(forceRefresh = false) {
    if (isLoading.value) {
      return
    }

    isLoading.value = true
    error.value = null

    try {
      // For personalized targeting, Remote Config uses the current user's identity
      // if they are signed in. Guest users will get default or generic values.
      const currentUser = auth.currentUser
      const currentUid = currentUser?.uid ?? null

      // Detect if user changed: either UID is different, or lastFetchedUid is null (after reset)
      const userChanged = currentUid !== null && currentUid !== lastFetchedUid.value

      // When forcing refresh, always clear persistence to ensure fresh data
      if (forceRefresh) {
        logger.log('Force refresh requested, clearing Remote Config persistence', {
          currentUid,
          lastFetchedUid: lastFetchedUid.value,
          userChanged,
          forceRefresh,
        })

        // Always clear cached ETag and other metadata from IndexedDB when forcing refresh
        // This ensures we bypass cache and get fresh data from the server
        await clearRemoteConfigPersistence()
      }

      const remoteConfig = await getRemoteConfig()

      // Only refresh token on force refresh to get latest custom claims
      if (forceRefresh && currentUser) {
        try {
          await currentUser.getIdToken(true) // Force refresh to get latest claims
        } catch (tokenError) {
          logger.warn('Failed to refresh auth token, continuing with existing token', tokenError)
          // Continue anyway - existing token might still work
        }
      }

      await fetchAndActivateRemoteConfig(remoteConfig, forceRefresh)
      if (forceRefresh) {
        logger.log('Remote Config refreshed for new user')
      }

      isActivated.value = true
      lastFetchTime.value = new Date()
      lastFetchedUid.value = currentUid
      fetchedForUid.value = currentUid // Track which user this config was fetched for
      try {
        testParameter.value = getValue(remoteConfig, 'test_parameter').asNumber()
      } catch {
        // keep default
      }

      logger.log('Remote Config fetched successfully', {
        uid: currentUid,
        userChanged,
        forceRefresh,
        lastFetchTime: lastFetchTime.value,
      })

      // Set up real-time listener if not already set up
      setupRealtimeListener(remoteConfig)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      const errorObj = err as Record<string, unknown>
      const errorCode = errorObj?.code as string | undefined

      error.value = errorMessage

      // Check if it's a 400 error (API not enabled or configuration issue)
      const is400Error =
        errorMessage.includes('400') ||
        errorMessage.includes('INVALID_ARGUMENT') ||
        errorCode === 'remote-config/fetch-status' ||
        errorCode === 'remoteconfig/fetch-status'

      if (is400Error) {
        // Retry logic for 400 errors (sometimes caused by race conditions in SDK state)
        if (!forceRefresh) {
          // If we weren't forcing refresh, try forcing it once
          logger.log('Retrying Remote Config fetch with forceRefresh due to 400 error')
          return await fetchConfig(true)
        }

        try {
          const remoteConfig = await getRemoteConfig()
          logger.error('Remote Config fetch failed (400/INVALID_ARGUMENT)', {
            errorCode,
            errorMessage,
            projectId: remoteConfig.app.options.projectId,
            appId: remoteConfig.app.options.appId,
            hint: 'This usually means the Firebase Remote Config API is not enabled in Google Cloud Console, or there is a mismatch between the App ID and Project ID.',
            enableApiUrl: `https://console.cloud.google.com/apis/library/firebaseremoteconfig.googleapis.com?project=${remoteConfig.app.options.projectId}`,
          })
        } catch {
          logger.error('Remote Config fetch failed (400/INVALID_ARGUMENT)', {
            errorCode,
            errorMessage,
          })
        }
      } else {
        logger.error('Remote Config fetch failed', { errorCode, errorMessage })
      }

      // Don't throw - allow app to continue with default values
      // Users can still call getString/getBoolean/getNumber which will return defaults
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Get a string value from Remote Config.
   * Ensures Analytics is initialized first.
   */
  async function getString(key: string, defaultValue = DEFAULT_STRING_VALUE): Promise<string> {
    try {
      const remoteConfig = await getRemoteConfig()
      return getValue(remoteConfig, key).asString()
    } catch (err) {
      logger.warn(`Failed to get Remote Config value: ${key}`, err)
      return defaultValue
    }
  }

  /**
   * Get a number value from Remote Config.
   * Ensures Analytics is initialized first.
   */
  async function getNumber(key: string, defaultValue = DEFAULT_NUMBER_VALUE): Promise<number> {
    try {
      const remoteConfig = await getRemoteConfig()
      return getValue(remoteConfig, key).asNumber()
    } catch (err) {
      logger.warn(`Failed to get Remote Config number: ${key}`, err)
      return defaultValue
    }
  }

  /**
   * Get a boolean value from Remote Config.
   * Ensures Analytics is initialized first.
   */
  async function getBoolean(key: string, defaultValue = DEFAULT_BOOLEAN_VALUE): Promise<boolean> {
    try {
      const remoteConfig = await getRemoteConfig()
      return getValue(remoteConfig, key).asBoolean()
    } catch (err) {
      logger.warn(`Failed to get Remote Config boolean: ${key}`, err)
      return defaultValue
    }
  }

  /**
   * Get all Remote Config values as an object.
   * Ensures Analytics is initialized first.
   */
  async function getAllValues(): Promise<Record<string, string>> {
    try {
      const remoteConfig = await getRemoteConfig()
      const all = getAll(remoteConfig)
      const result: Record<string, string> = {}
      Object.keys(all).forEach((key) => {
        result[key] = all[key].asString()
      })
      return result
    } catch (err) {
      logger.error('Failed to get all Remote Config values', err)
      return {}
    }
  }

  /**
   * Get the source of the value (remote, default, or static).
   * Ensures Analytics is initialized first.
   */
  async function getValueSource(key: string): Promise<'remote' | 'default' | 'static'> {
    try {
      const remoteConfig = await getRemoteConfig()
      return getValue(remoteConfig, key).getSource()
    } catch (err) {
      logger.warn(`Failed to get value source: ${key}`, err)
      return 'default'
    }
  }

  /**
   * Get Firebase app options (projectId, appId, apiKey) from Remote Config instance.
   * Ensures Analytics is initialized first.
   */
  async function getAppOptions(): Promise<{ projectId: string; appId: string; apiKey?: string }> {
    try {
      const remoteConfig = await getRemoteConfig()
      return {
        projectId: remoteConfig.app.options.projectId ?? '',
        appId: remoteConfig.app.options.appId ?? '',
        apiKey: remoteConfig.app.options.apiKey,
      }
    } catch (err) {
      logger.warn('Failed to get Remote Config app options', err)
      return { projectId: '', appId: '', apiKey: undefined }
    }
  }

  /**
   * Clean up real-time listener (called on unmount or reset).
   */
  function onUnmounted() {
    if (realtimeUnsubscribe) {
      realtimeUnsubscribe()
      realtimeUnsubscribe = null
      logger.log('Remote Config real-time listener cleaned up')
    }
  }

  return {
    isLoading: computed(() => isLoading.value),
    lastFetchTime: computed(() => lastFetchTime.value),
    error: computed(() => error.value),
    isActivated: computed(() => isActivated.value),
    testParameter,
    fetchedForUid: computed(() => fetchedForUid.value), // Expose fetched user UID for debugging
    fetchConfig,
    resetForNewUser,
    onUnmounted,
    getString,
    getNumber,
    getBoolean,
    getAllValues,
    getValueSource,
    getAppOptions,
  }
})
